import { cx } from 'emotion';
import React from 'react';
import PropTypes from 'prop-types';

import { Menu, ListItem, Tooltip } from '@ublend-npm/aulaui-next';

import MoreVert from '@mui/icons-material/MoreVert';
import {
  MATERIAL_PERMISSIONS,
  getPermissionDeniedMessage,
  evaluateMaterialAction,
} from '@core/materials/permissions';

import { menu, menuItem, dangerMenuItem } from '../../../common/Menu.styled';
import { HoverIconButton } from './MaterialListItem.styled';

const MORE_OPTIONS_TOOLTIP = 'Section options';

const Actions = {
  Rename: 'rename',
  Schedule: 'schedule',
  ChangeSchedule: 'changeSchedule',
  KeepHidden: 'keepHidden',
  PublishNow: 'publishNow',
  Delete: 'delete',
};

const MenuItems = [
  {
    key: Actions.Rename,
    label: 'Rename',
    isVisible: () => true,
    danger: false,
    action: 'RENAME_SECTION',
  },
  {
    key: Actions.Schedule,
    label: 'Schedule',
    isVisible: ({ hidden, scheduledFor, educatorOnly }) =>
      !educatorOnly && !!hidden && !scheduledFor,
    danger: false,
    action: 'SCHEDULE_SECTION',
  },
  {
    key: Actions.ChangeSchedule,
    label: 'Change scheduled time',
    isVisible: ({ hidden, scheduledFor }) => !!hidden && !!scheduledFor,
    danger: false,
    action: 'SCHEDULE_SECTION',
  },
  {
    key: Actions.KeepHidden,
    label: 'Undo scheduling and keep hidden',
    isVisible: ({ hidden, scheduledFor }) => !!hidden && !!scheduledFor,
    danger: false,
    action: 'PUBLISH_SECTION',
  },
  {
    key: Actions.PublishNow,
    label: 'Publish now',
    isVisible: ({ hidden, scheduledFor }) => !!hidden && !!scheduledFor,
    danger: false,
    action: 'PUBLISH_SECTION',
  },
  {
    key: Actions.Delete,
    label: 'Delete',
    isVisible: () => true,
    danger: true,
    action: 'DELETE_SECTION',
  },
];

const FolderListItemMenu = (props) => {
  const {
    ariaLabelId,
    openRenameDialog,
    openDeleteDialog,
    openScheduleDialog,
    keepHidden,
    publishNow,
    hidden,
    scheduledFor,
    educatorOnly,
    canEdit: propCanEdit = true,
    canDelete: propCanDelete = true,
    canAccessEducatorArea = false,
    onRestorePage,
    permissions = [],
    isStudent = false,
    isRbacEnabled = false,
  } = props;

  // In educator area: if user has educator area permission, they can do everything
  // Skip all permission checks when in educator area
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  // If in educator area and user has educator area permission, allow rename and delete
  // This includes users who only have educator area permission (not edit/delete)
  const hasEducatorAreaAccess = educatorOnly && canAccessEducatorArea;

  const evaluateAction = (actionKey, fallbackAllowed) => {
    // If user has educator area permission, allow all actions in educator area
    if (hasEducatorAreaAccess) {
      return { allowed: true };
    }
    return evaluateMaterialAction(actionKey, {
      permissions: effectivePermissions,
      fallback: fallbackAllowed,
    });
  };

  const renameStatus = evaluateAction('RENAME_SECTION', propCanEdit);
  const deleteStatus = evaluateAction(
    'DELETE_SECTION',
    propCanDelete && Boolean(openDeleteDialog)
  );
  const scheduleStatus = evaluateAction(
    'SCHEDULE_SECTION',
    propCanEdit && Boolean(openScheduleDialog)
  );
  const publishStatus = evaluateAction(
    'PUBLISH_SECTION',
    propCanEdit && Boolean(publishNow)
  );

  // Build all menu items with their status
  const menuItemsWithStatus = MenuItems.map((item) => {
    if (!item.isVisible({ hidden, scheduledFor, educatorOnly })) {
      return null;
    }

    let status;
    switch (item.action) {
      case 'RENAME_SECTION':
        status = renameStatus;
        break;
      case 'DELETE_SECTION':
        status = deleteStatus;
        break;
      case 'SCHEDULE_SECTION':
        status = scheduleStatus;
        break;
      case 'PUBLISH_SECTION':
        status = publishStatus;
        break;
      default:
        status = { allowed: true };
    }

    return { ...item, status };
  }).filter(Boolean);

  // For students: filter menu items to only show ones they have permission for
  // For instructors: show all items (may be disabled)
  // If user has educator area permission, show all menu items (full access)
  const filteredMenuItems = menuItemsWithStatus.filter((mItem) => {
    if (isStudent && isRbacEnabled) {
      return mItem.status.allowed;
    }
    // If user has educator area permission, show all items (full access)
    if (hasEducatorAreaAccess) {
      return true;
    }
    return true;
  });

  // For students: only show menu if they have at least one permission
  // For instructors: always show menu (items may be disabled)
  // Show menu if user has educator area permission even without edit/delete
  const hasAnyPermission = filteredMenuItems.length > 0;
  // Always show menu if user has educator area access
  const shouldShowMenu = hasEducatorAreaAccess
    ? true
    : isStudent
    ? isRbacEnabled && hasAnyPermission
    : hasAnyPermission;

  const handleSelect = ({ item }) => {
    switch (item) {
      case Actions.Rename:
        if (renameStatus.allowed) openRenameDialog();
        break;
      case Actions.Delete:
        if (deleteStatus.allowed) openDeleteDialog();
        break;
      case Actions.Schedule:
      case Actions.ChangeSchedule:
        if (scheduleStatus.allowed) openScheduleDialog();
        break;
      case Actions.KeepHidden:
        if (publishStatus.allowed) keepHidden();
        break;
      case Actions.PublishNow:
        if (publishStatus.allowed) publishNow();
        break;
      default:
        break;
    }
  };

  // Don't render menu if student has no permissions
  if (!shouldShowMenu) {
    return null;
  }

  return (
    <Menu
      data-testid="folder-menu"
      classes={{ menu }}
      ariaLabelledby={ariaLabelId}
      renderTarget={({ handleTargetClick }) => (
        <Tooltip title={MORE_OPTIONS_TOOLTIP} position="top">
          <div>
            <HoverIconButton
              icon={MoreVert}
              size="xSmall"
              type="secondary"
              label={MORE_OPTIONS_TOOLTIP}
              onClick={handleTargetClick}
              position="top"
            />
          </div>
        </Tooltip>
      )}
      onSelect={handleSelect}
      renderChildren={({ getItemProps, highlightedIndex }) =>
        filteredMenuItems.map((item, index) => {
          const status = item.status;

          const listItem = (
            <ListItem
              attributes={{ 'data-testid': 'folder-menu-item' }}
              key={item.key}
              className={item.danger ? cx(menuItem, dangerMenuItem) : menuItem}
              primaryText={item.label}
              highlighted={highlightedIndex === index}
              disabled={!status.allowed}
              {...getItemProps({
                item: status.allowed ? item.key : null,
                onClick: (e) => {
                  if (!status.allowed) {
                    e.stopPropagation();
                  }
                },
              })}
            />
          );

          return status.message && !status.allowed ? (
            <Tooltip
              title={status.message}
              key={`${item.key}-tooltip`}
              position="bottom"
            >
              <div>{listItem}</div>
            </Tooltip>
          ) : (
            listItem
          );
        })
      }
    />
  );
};

FolderListItemMenu.propTypes = {
  hidden: PropTypes.bool,
  educatorOnly: PropTypes.bool,
  scheduledFor: PropTypes.string,
  ariaLabelId: PropTypes.string,
  openRenameDialog: PropTypes.func.isRequired,
  openDeleteDialog: PropTypes.func.isRequired,
  openScheduleDialog: PropTypes.func.isRequired,
  keepHidden: PropTypes.func.isRequired,
  publishNow: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  canAccessEducatorArea: PropTypes.bool,
  onRestorePage: PropTypes.func,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isStudent: PropTypes.bool,
  isRbacEnabled: PropTypes.bool,
};

FolderListItemMenu.defaultProps = {
  ariaLabelId: null,
  hidden: false,
  educatorOnly: false,
  scheduledFor: null,
  canEdit: true,
  canDelete: true,
  canAccessEducatorArea: false,
  onRestorePage: null,
  permissions: [],
  isStudent: false,
  isRbacEnabled: false,
};

export default FolderListItemMenu;
